import React, { useEffect, useRef } from "react";
import { HashRouter as Router, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import SignupAndLogin from "./SignupAndLogin/SignupAndLogin";
import "./App.css";
import NewLayout from "./Layout/Layout.js";
import InstallButton from "./containers/install/InstallButton";
import { pages } from "./RoutesData.js";
import { rejectedNotice } from "./SignupAndLogin/loginSlice.js";

/**
 * The old `containers/Billing.js` modal used to be wired in here. It has been
 * unhooked, not deleted — the file is still on disk if anyone wants to look
 * at it.
 *
 * It was dead code: `showHandler` opened it when `user.notify` was `loop` or
 * `stuck`, but `notify` sits on `user.business`, not on the user, so the test
 * was never true and the modal never appeared. It could not have taken a
 * payment anyway — it was a notice with an OK button — and it rendered
 * outside the Router, so it could only ever have shown once at sign-in.
 *
 * The monthly bill now lives in containers/billing/, mounted inside the
 * layout so it can follow tab changes. Leaving the old one connected would
 * mean two dialogs the day somebody set the wrong field.
 */
function App() {
  const dispatch = useDispatch();

  const isLogin = useSelector((state) => state.login.isLogin);

  // The interceptor is installed once and has to see the CURRENT sign-in
  // state, not the one captured when it was created.
  const isLoginRef = useRef(isLogin);
  useEffect(() => {
    isLoginRef.current = isLogin;
  }, [isLogin]);

  /**
   * A token the server stops accepting mid-shift — the account was disabled,
   * the password changed on another machine — shows up as the first 401. That
   * signs the person out with an explanation rather than leaving them in an
   * app where nothing loads.
   *
   * 401 only: 403 is what `restrictTo` returns for a legitimate permission
   * refusal, and being told off for opening the wrong screen must not log
   * anyone out.
   */
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = String(error?.config?.url || "");
        const isSignInCall = url.indexOf("/users/authenticate") !== -1;

        if (status === 401 && !isSignInCall && isLoginRef.current) {
          dispatch(rejectedNotice());
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(id);
  }, [dispatch]);

  return (
    <div
      className="App"
      style={{
        backgroundColor: "white",
        display: "flex",
        justifyContent: "center",
      }}
    >
      {/* Offers to install the app on this computer, bottom right. It draws
          nothing unless the browser says the app can actually be installed,
          so it is out of the way on a till that is already installed and on
          a browser that cannot install at all. Mounted here rather than
          inside the layout so it is offered on the login screen too — which
          is where a new machine is most likely to be set up. */}
      <InstallButton />

      {isLogin ? (
        <Router>
          <NewLayout>
            <Routes>{pages?.map((page) => page)}</Routes>
          </NewLayout>
        </Router>
      ) : (
        <SignupAndLogin />
      )}
    </div>
  );
}

export default App;
